import FButtonPrevious from 'components/BaseComponents/FButton/FPreviousButton'
import FImage from 'components/BaseComponents/FImage'
import SBox from 'components/opinionless/SBox'
import SInputError from 'components/opinionless/SInputErrorV2'
import SInput from 'components/opinionless/SInputV2'
import { WIZARD_STEPS_MAPPING } from 'containers/Apply/LongBeachWizard'
import { updatePercentageAndStep } from 'v2/components/XProgressBar/utils'
import { LBButton, LBText } from 'containers/Apply/steps/ApplicationStepLBIntroduction'
import { useTranslation } from 'i18n'
import React, { useContext, useEffect, useState } from 'react'
import ReactDatePicker from 'react-datepicker'
import { Controller, useForm } from 'react-hook-form'
import { StepWizardChildProps } from 'react-step-wizard'
import MaskedInput from 'react-text-mask'
import { SStyled, useSTheme } from 'styles/theme'
import { useSaveHouseholdMembersMutation } from 'types/graphql'
import { formattedDateMMDDYYYY, formattedDateYYYYMMDD, maximum100YearsOfAge } from 'utils/date'
import { ga4_useTitle } from 'utils/ga4'
import XBanner from 'v2/components/XBanner'
import { ApplicationContext } from 'v2/context/application'
import { useApplicationBasicInfo } from 'v2/hooks/useApplicationBasicInfo'
import { useHouseholdMembers } from 'v2/hooks/useHouseholdMembers'
import style from './style.module.scss'

interface Props {
    setCurrentStep?: (number) => void
    applicationId: string
    personId: string
    fontFamily: any
    onNextStep?: any
    onPrevious?: any
    updatePercentage: any
}

const Container = SStyled(SBox)`
  width: 100%;
  max-width: 600px;
`

export const HOUSEHOLD_MEMBER_RELATIONSHIP = {
    ssi_benefits_wizard_relationship_spouse: 'long_beach_household_spouse_label',
    ssi_benefits_wizard_relationship_child: 'long_beach_household_child_label',
    ssi_benefits_wizard_relationship_parent: 'long_beach_household_parent_label',
    ssi_benefits_wizard_relationship_other: 'long_beach_household_other_label',
}

const HouseholdIncomeMembersDOB: React.FC<Props> = (props: Props & StepWizardChildProps) => {
    const applicationContext = useContext(ApplicationContext)

    const {
        loading: householdMembersLoading,
        error: householdMembersError,
        refetch: householdMembersRefetch,
    } = useHouseholdMembers()
    useApplicationBasicInfo()

    useEffect(() => {
        householdMembersRefetch()
    }, [householdMembersRefetch])

    const theme = useSTheme()
    const { t } = useTranslation(window.LANG_NS)
    const { handleSubmit, errors, setError, control } = useForm()
    const [saveHouseholdMembers, { loading: saveHouseholdMembersLoading }] = useSaveHouseholdMembersMutation()
    const [householdMembersDetails, setHouseholdMembersDetails] = useState([])
    const [loading, setLoading] = useState(true)

    ga4_useTitle('Household Income Members DOB')

    useEffect(() => {
        if (!householdMembersLoading) {
            setLoading(false)
            setHouseholdMembersDetails(
                applicationContext.householdMembers.map((member) => ({
                    id: member.id,
                    name: member.name,
                    relation: member.relation,
                    dob: member.dob,
                    blindDisabled: member.blindDisabled,
                }))
            )
        }
    }, [applicationContext.householdMembers])

    const fontFamily = props.fontFamily

    const updateDobCallback = (index: number, dob: Date) => {
        const updatedMembers = [...householdMembersDetails]
        updatedMembers[index].dob = formattedDateYYYYMMDD(dob, '-')
        setHouseholdMembersDetails(updatedMembers)
    }

    const getDateInput = (member: any, index: number) => {
        const id = `dob-field_${index}`
        const wrapper_id = `dob-wrapper_${index}`
        return (
            <SBox flex={1} id={wrapper_id}>
                <Controller
                    control={control}
                    name={id}
                    defaultValue={member?.dob ? new Date(Date.parse(member?.dob)) : null}
                    rules={{ required: 'required_field' as string, validate: maximum100YearsOfAge }}
                    render={({ onChange, value }) => (
                        <ReactDatePicker
                            maxDate={new Date(new Date().setMonth(new Date().getMonth() + 9))}
                            onSelect={(dob) => {
                                onChange(dob)
                                updateDobCallback(index, dob)
                            }}
                            selected={value}
                            placeholderText={t('dob_placeholder')}
                            onChange={(dob: Date) => {
                                onChange(dob)
                                updateDobCallback(index, dob)
                            }}
                            customInput={
                                <MaskedInput
                                    mask={[/\d/, /\d/, '/', /\d/, /\d/, '/', /\d/, /\d/, /\d/, /\d/]}
                                    render={(ref, props) => (
                                        <div style={{ position: 'relative' }}>
                                            <SInput
                                                sx={{
                                                    borderColor: errors[id]
                                                        ? theme.colors.redError
                                                        : theme.colors.inputBorder,
                                                }}
                                                name={id}
                                                id={id}
                                                ref={ref}
                                                {...props}
                                            />
                                            <div
                                                style={{
                                                    position: 'absolute',
                                                    top: '50%',
                                                    transform: 'translateY(-50%)',
                                                    right: '15px',
                                                    width: '24px',
                                                    pointerEvents: 'none',
                                                }}
                                            >
                                                <FImage imageUrl={'/dob_field_icon.svg'} alt="person_bw" />
                                            </div>
                                        </div>
                                    )}
                                />
                            }
                        />
                    )}
                />
                {errors[id] && (
                    <SInputError
                        sx={{
                            fontFamily: fontFamily.body,
                            color: theme.colors.redError,
                        }}
                        forElementId={wrapper_id}
                    >
                        {t(errors[id]?.message)}
                    </SInputError>
                )}
            </SBox>
        )
    }

    const handleHouseholdMembersDOB = () => {
        saveHouseholdMembers({
            variables: {
                personId: applicationContext.profile.person.id,
                householdMembers: householdMembersDetails,
            },
        }).then((response) => {
            if (response.data.saveHouseholdMembers.success) {
                props.nextStep()
            } else {
                setError('householdMembersDOB', { type: 'oops', message: t('error') })
            }
        })
    }

    const { percentage, wizardStep, childWizzardStep } = updatePercentageAndStep(
        WIZARD_STEPS_MAPPING.householdInfo,
        WIZARD_STEPS_MAPPING.householdInfo.dob,
        WIZARD_STEPS_MAPPING
    )
    props.updatePercentage(percentage, wizardStep, childWizzardStep)

    if (loading) {
        return null
    }

    return (
        <Container>
            <SBox as="form" onSubmit={handleSubmit(handleHouseholdMembersDOB)}>
                <FButtonPrevious onClick={props.previousStep} />
                <LBText
                    sx={{
                        textAlignLast: 'left',
                        whiteSpace: 'pre-line',
                        fontFamily: fontFamily.body,
                        fontWeight: '800',
                        lineHeight: '140%',
                        color: '#202020',
                        fontSize: '16px',
                        marginBottom: '24px',
                    }}
                >
                    {t('long_beach_household_income_members_dob_subtitle')}
                </LBText>

                <LBText
                    sx={{
                        textAlignLast: 'left',
                        whiteSpace: 'pre-line',
                        fontFamily: fontFamily.body,
                        fontWeight: '400',
                        lineHeight: '140%',
                        color: theme.colors.gray400,
                        fontSize: '14px',
                        marginBottom: '24px',
                    }}
                    dangerouslySetInnerHTML={{
                        __html: t('long_beach_household_income_introduction_members_dob_description'),
                    }}
                />

                <div className={style.household_applicant_dob_container}>
                    <div className={style.household_applicant_dob_container__icon}>
                        <FImage imageUrl={'/lb_household_income_person.svg'} alt="person" />
                    </div>
                    <div className={style.household_applicant_dob_container__applicant_group}>
                        <LBText
                            sx={{
                                textAlignLast: 'left',
                                whiteSpace: 'pre-line',
                                fontFamily: fontFamily.body,
                                fontWeight: '700',
                                lineHeight: '140%',
                                color: theme.colors.lbBlue,
                                fontSize: '14px',
                                marginBottom: '5px',
                            }}
                            dangerouslySetInnerHTML={{
                                __html: t('long_beach_applicant_name_label_relationship', {
                                    name: `${applicationContext?.profile?.firstName} ${applicationContext?.profile?.lastName}`,
                                    relationship: t('long_beach_household_applicant_label'),
                                }),
                            }}
                        />
                        <SInput
                            name="householdApplicantDOB"
                            id="householdApplicantDOB"
                            disabled
                            value={formattedDateMMDDYYYY(applicationContext?.profile?.dob?.toString())}
                            sx={{
                                fontFamily: fontFamily.body,
                                borderRadius: '8px',
                                marginBottom: '5px',
                            }}
                            style={{
                                fontSize: '14px',
                                fontWeight: 'normal',
                                lineHeight: '20px',
                                color: theme.colors.gray400,
                                backgroundColor: theme.colors.grayUnselectedCheckboxBackground,
                            }}
                        />
                    </div>
                </div>
                {householdMembersDetails.map((member, index) => (
                    <div className={style.household_member_dob_container} key={member.id}>
                        <div className={style.household_member_dob_container__icon}>
                            <FImage imageUrl={'/lb_household_income_person_bw.svg'} alt="person_bw" />
                        </div>
                        <div className={style.household_member_dob_container__member_group}>
                            <LBText
                                sx={{
                                    textAlignLast: 'left',
                                    whiteSpace: 'pre-line',
                                    fontFamily: fontFamily.body,
                                    fontWeight: '700',
                                    lineHeight: '140%',
                                    color: theme.colors.gray400,
                                    fontSize: '14px',
                                    marginBottom: '5px',
                                }}
                                dangerouslySetInnerHTML={{
                                    __html: t('long_beach_member_name_label_relationship', {
                                        name: `${member.name}`,
                                        relationship: t(`${HOUSEHOLD_MEMBER_RELATIONSHIP[member.relation]}`),
                                    }),
                                }}
                            />
                            {getDateInput(member, index)}
                        </div>
                    </div>
                ))}

                <LBButton
                    type="submit"
                    id="continueButton"
                    loading={saveHouseholdMembersLoading}
                    title={t('save_and_continue')}
                    width="100%"
                    mt={7}
                    mb={5}
                    textStyle={{
                        fontWeight: 700,
                        fontSize: '14px',
                        lineHeight: '140%',
                        fontFamily: fontFamily.body,
                        color: theme.colors.white,
                    }}
                >
                    {t('save_and_continue')}{' '}
                </LBButton>
                {errors.householdMembersDOB && (
                    <SInputError
                        sx={{
                            fontFamily: fontFamily.body,
                            color: theme.colors.redError,
                        }}
                        forElementId="continueButton"
                    >
                        {t(errors.householdMembersDOB?.message)}
                    </SInputError>
                )}
            </SBox>
        </Container>
    )
}

export default HouseholdIncomeMembersDOB
